import { Box, Avatar, Chip, Tooltip, Typography, Alert } from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { ReviewHeader } from "./header";
import { ReviewActions } from "./actions";
import { ReviewEditDraft } from "./edit-draft";
import { ReviewStatusAlert } from "./alert";
import { ReviewInput } from "./input";
import { VendorReply } from "./vendor-reply";

export const ReviewListItem = ({
  review,
  user,
  editingId,
  editData,
  onStartEdit,
  onCancelEdit,
  onEditChange,
  onEditSubmit,
  onDelete,
  onDeleteDraft,
  onLike,
  onDislike,
  onReport,
  onHistory,
  showUserReviewsOnly = false,
  restaurant = null,
}) => {
  const isEditing = editingId === review.id;
  const isOwnReview = user && review.userId === user.uid;
  const isVerifiedUser = user && user.emailVerified && user.phoneVerified;
  const canEdit =
    isOwnReview && ["pendingCreate", "approved", "pendingEdit"].includes(review.status);
  const canDelete =
    isOwnReview && !["reported", "pendingReport", "deleted", "rejected"].includes(review.status);

  const getLatestRejectionReason = (review) => {
    return review.latestRejection?.reason || null;
  };

  const isPendingReportOwner =
    user &&
    review.reportedBy?.length > 0 &&
    review.reportedBy[review.reportedBy.length - 1].status === "pendingReport" &&
    review.reportedBy[review.reportedBy.length - 1].userId === user.uid;

  const ownPendingReport = isPendingReportOwner
    ? review.reportedBy[review.reportedBy.length - 1]
    : null;

  if (isEditing) {
    return (
      <Box display="flex" alignItems="flex-start" gap={2} py={2}>
        <Avatar src={review.avatar} alt={review.name} />
        <Box flexGrow={1}>
          <Box display="flex" alignItems="center" gap={0.5} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              {showUserReviewsOnly ? review.dealTitle || review.restaurantName : review.name}
            </Typography>
            <Chip label="Editing" size="small" color="info" />
            {isVerifiedUser && (
              <Tooltip title="Verified User">
                <VerifiedUserIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            )}
          </Box>
          <ReviewInput
            formData={editData}
            setFormData={onEditChange}
            onSubmit={() => onEditSubmit(review.id)}
            isSubmitting={false}
            isEditing={true}
            onCancel={onCancelEdit}
          />
        </Box>
      </Box>
    );
  }

  const shouldShowAlert =
    (isOwnReview &&
      ["pendingCreate", "pendingEdit", "pendingDelete", "reported"].includes(review.status)) ||
    isPendingReportOwner;

  const rejectionReason = getLatestRejectionReason(review);

  return (
    <Box display="flex" alignItems="flex-start" gap={2} py={2}>
      <Avatar src={review.avatar} alt={review.name} />
      <Box flexGrow={1}>
        <ReviewHeader
          review={review}
          isOwnReview={isOwnReview}
          isVerifiedUser={isVerifiedUser}
          canEdit={canEdit}
          canDelete={canDelete}
          onStartEdit={onStartEdit}
          onDelete={onDelete}
          showUserReviewsOnly={showUserReviewsOnly}
        />
        <Typography
          variant="body2"
          mt={1}
          sx={{
            wordWrap: "break-word",
            overflowWrap: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {review.description}
        </Typography>

        {isOwnReview && review.status === "rejected" && rejectionReason && (
          <Alert severity="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
            <Typography variant="caption" fontWeight="bold">Reject reason:</Typography>
            <br />
            {rejectionReason}
            {review.canResubmit && (
              <>
                <br />
                <Typography variant="caption" color="text.secondary">
                  You can edit and resubmit this review.
                </Typography>
              </>
            )}
          </Alert>
        )}

        <ReviewActions
          review={review}
          user={user}
          isOwnReview={isOwnReview}
          showUserReviewsOnly={showUserReviewsOnly}
          onLike={onLike}
          onDislike={onDislike}
          onReport={onReport}
          onHistory={onHistory}
          restaurant={restaurant}
        />

        {isOwnReview && review.status === "pendingEdit" && review.editDraft && (
          <ReviewEditDraft
            review={review}
            canEdit={canEdit}
            onStartEdit={onStartEdit}
            onDeleteDraft={onDeleteDraft}
          />
        )}

        {shouldShowAlert && (
          <ReviewStatusAlert
            review={review}
            isOwnReview={isOwnReview}
            ownPendingReport={ownPendingReport}
          />
        )}

        {/* Vendor reply — always last in the thread */}
        {review.vendorReply && <VendorReply reply={review.vendorReply} />}
      </Box>
    </Box>
  );
};